import type { PurchaseRequisition, PurchaseRequisitionStatus } from '../types/purchasing';

export const PURCHASE_STATUS_LABEL: Record<PurchaseRequisitionStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending approval',
  in_review: 'In review',
  approved: 'PO issued',
  purchased: 'Purchased',
  returned: 'Returned',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

export type PurchaseStatusFilter = 'open' | 'approved' | 'purchased' | 'closed' | 'active' | 'all';

export function isPurchaseHeld(row: Pick<PurchaseRequisition, 'hold'>): boolean {
  return !!row.hold;
}

/**
 * Hugh files the approval chain and leaves the CEO (or other approver) pending,
 * but older rows were stored as drafts. A quiet Caesar draft
 * (`purchasing_agent_bill_workflow`) stays a draft.
 */
export function agentDraftAlreadyInApproval(
  row: Pick<PurchaseRequisition, 'status' | 'createdViaAgent' | 'source' | 'approvalSteps'>,
): boolean {
  if (row.status !== 'draft' || !row.createdViaAgent) return false;
  if (row.source === 'purchasing_agent_bill_workflow') return false;
  return row.approvalSteps.some((step) => step.status === 'pending');
}

export function purchaseListStatus(row: PurchaseRequisition): { id: string; label: string } {
  if (row.status === 'purchased') return { id: 'purchased', label: 'Purchased' };
  if (row.hold) return { id: 'held', label: row.poNumber ? 'PO on hold' : 'On hold' };
  if (row.status === 'approved' && row.purchaseVariance) {
    return { id: 'variance', label: 'Actual review' };
  }
  if (agentDraftAlreadyInApproval(row)) return { id: 'pending_approval', label: 'Pending approval' };
  return { id: row.status, label: PURCHASE_STATUS_LABEL[row.status] };
}

/**
 * Approver return for a purchase an agent created on someone's behalf.
 * Pending CRM requisitions already use the normal return button. This covers
 * agent drafts (the approver is assigned but the row is not submitted) and
 * agent POs that were issued or recorded as purchased with no open step.
 * The document goes back to `requesterId` — the CRM user who asked for it.
 */
export function canReturnAgentCreatedPurchase(input: {
  createdViaAgent?: boolean;
  alreadyPurchased?: boolean;
  status: PurchaseRequisitionStatus | string;
  requesterId?: string;
  actorId: string;
  /** The actor is the current pending approver, or may act in their place. */
  actorIsPendingApprover: boolean;
  /** On the approval trail, or purchasing leadership (admin / COO / CFO). */
  actorCanReview: boolean;
  /** A QuickBooks bill or supplier-bill close is already attached. */
  blockedByBill?: boolean;
}): boolean {
  if (!input.createdViaAgent || input.blockedByBill) return false;
  const requesterId = input.requesterId?.trim();
  if (!requesterId || requesterId === input.actorId) return false;
  if (input.status === 'returned' || input.status === 'rejected' || input.status === 'cancelled') return false;
  if (input.status === 'draft' || input.status === 'pending_approval' || input.status === 'in_review') {
    return input.actorIsPendingApprover;
  }
  if (input.status === 'approved') return input.actorCanReview;
  if (input.status === 'purchased' && input.alreadyPurchased) return input.actorCanReview;
  return false;
}

/**
 * CRM #73 — return an issued (approved) PO to the requester so they can change
 * store / re-source. Works for CRM and agent POs. Withdraws PO numbers on return.
 * Pending returns still use the standard "Return for edits" path.
 */
export function canReturnIssuedPurchaseToRequester(input: {
  status: PurchaseRequisitionStatus | string;
  requesterId?: string;
  actorId: string;
  actorCanReview: boolean;
  blockedByBill?: boolean;
}): boolean {
  if (input.blockedByBill) return false;
  if (input.status !== 'approved') return false;
  if (!input.actorCanReview) return false;
  const requesterId = input.requesterId?.trim();
  if (!requesterId || requesterId === input.actorId) return false;
  return true;
}

export function isAwaitingReviewer(row: PurchaseRequisition, userId: string | undefined): boolean {
  if (!userId) return false;
  if (row.status === 'returned') return row.requesterId === userId;
  if (row.status === 'purchased' || row.status === 'cancelled' || row.status === 'rejected') return false;
  if (row.hold) {
    return row.currentApproverId === userId;
  }
  if (row.status === 'approved' && row.purchaseVariance?.reviewerId === userId) return true;
  if (row.status === 'pending_approval' || row.status === 'in_review' || agentDraftAlreadyInApproval(row)) {
    return row.approvalSteps.find(s => s.status === 'pending')?.userId === userId;
  }
  return false;
}

export function matchesPurchaseStatusFilter(
  row: PurchaseRequisition,
  filter: PurchaseStatusFilter,
): boolean {
  if (filter === 'all') return true;
  // Active = everything still in play (not cancelled/rejected). Closed POs
  // that finished purchasing stay visible; rejected/cancelled do not.
  if (filter === 'active') {
    return row.status !== 'rejected' && row.status !== 'cancelled';
  }
  if (filter === 'open') {
    if (row.status === 'purchased' || row.status === 'rejected' || row.status === 'cancelled') return false;
    if (row.hold) return true;
    if (row.status === 'approved' && row.purchaseVariance) return true;
    return row.status === 'draft' || row.status === 'pending_approval' || row.status === 'in_review' || row.status === 'returned';
  }
  if (filter === 'approved') return row.status === 'approved' && !row.hold;
  if (filter === 'purchased') return row.status === 'purchased';
  if (filter === 'closed') return row.status === 'rejected' || row.status === 'cancelled';
  return true;
}

export function canHoldPurchaseStatus(status: PurchaseRequisitionStatus): boolean {
  return status === 'pending_approval' || status === 'in_review' || status === 'approved';
}

export function canMarkPurchasedStatus(status: PurchaseRequisitionStatus): boolean {
  return status === 'approved';
}
